import * as React from "react";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

export interface DropdownOption<T extends string> {
  value: T;
  label: string;
  description?: string;
}

export function Dropdown<T extends string>({
  label,
  value,
  options,
  onChange,
  className,
  disabled = false,
}: {
  label: string;
  value: T;
  options: DropdownOption<T>[];
  onChange: (value: T) => void;
  className?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(0);
  const ref = React.useRef<HTMLDivElement>(null);
  const selected = options.find((option) => option.value === value) || options[0];
  const selectedIndex = Math.max(
    0,
    options.findIndex((option) => option.value === selected.value),
  );

  React.useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  React.useEffect(() => {
    if (open) setActiveIndex(selectedIndex);
  }, [open, selectedIndex]);

  function choose(index: number) {
    if (disabled) return;
    const option = options[index];
    if (!option) return;
    onChange(option.value);
    setOpen(false);
  }

  function moveActive(delta: number) {
    if (disabled) return;
    setOpen(true);
    setActiveIndex((current) => (current + delta + options.length) % options.length);
  }

  return (
    <div ref={ref} className={cn("relative min-w-0", className)}>
      <button
        type="button"
        className="flex min-h-11 w-full min-w-0 items-center justify-between gap-3 rounded-lg border border-border bg-white px-3 py-2 text-left text-sm font-medium text-ink shadow-sm outline-none transition-colors hover:bg-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
        aria-controls={`${label.replace(/\s+/g, "-").toLowerCase()}-listbox`}
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown") {
            event.preventDefault();
            moveActive(1);
          } else if (event.key === "ArrowUp") {
            event.preventDefault();
            moveActive(-1);
          } else if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            if (open) {
              choose(activeIndex);
            } else {
              setOpen(true);
            }
          } else if (event.key === "Escape") {
            setOpen(false);
          }
        }}
      >
        <span className="truncate">{selected.label}</span>
        <ChevronDown className={cn("size-4 shrink-0 text-muted transition-transform", open && "rotate-180")} aria-hidden="true" />
      </button>
      {open ? (
        <div
          id={`${label.replace(/\s+/g, "-").toLowerCase()}-listbox`}
          className="absolute left-0 right-0 z-40 mt-2 max-h-72 overscroll-contain overflow-auto rounded-lg border border-border bg-white p-1 shadow-popover"
          role="listbox"
        >
          {options.map((option, index) => (
            <button
              type="button"
              key={option.value}
              role="option"
              aria-selected={option.value === value}
              className={cn(
                "flex min-h-11 w-full items-start gap-2 rounded-md px-3 py-2 text-left text-sm text-ink hover:bg-surface focus:bg-surface focus:outline-none",
                index === activeIndex && "bg-surface",
              )}
              onMouseEnter={() => setActiveIndex(index)}
              onPointerDown={(event) => {
                if (event.pointerType === "mouse") {
                  event.preventDefault();
                }
              }}
              onClick={() => {
                choose(index);
              }}
              onKeyDown={(event) => {
                if (event.key === "ArrowDown") {
                  event.preventDefault();
                  moveActive(1);
                } else if (event.key === "ArrowUp") {
                  event.preventDefault();
                  moveActive(-1);
                } else if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  choose(index);
                } else if (event.key === "Escape") {
                  setOpen(false);
                }
              }}
            >
              <Check className={cn("mt-0.5 size-4 shrink-0 text-credit", option.value === value ? "opacity-100" : "opacity-0")} />
              <span className="min-w-0">
                <span className="block font-medium">{option.label}</span>
                {option.description ? <span className="block text-xs leading-5 text-muted">{option.description}</span> : null}
              </span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
